import React, { useMemo } from "react";
import {
  Alert,
  Table,
  Text,
  Badge,
  Stack,
  Group,
  Box,
  Accordion,
} from "@mantine/core";
import moment from "moment";
import EntityTabAnchor from "./EntityTabAnchor";

const getSourceTypeColor = (sourceType) => {
  switch (sourceType) {
    case "CLIENTPAYMENT":
    case "ADJUSTMENT":
      return "blue";
    case "COMMISSIONSTATEMENT":
      return "violet";
    case "INCENTIVE":
      return "teal";
    default:
      return "gray";
  }
};

const formatDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD") : "-";

const getConsultantLabel = (consultant) =>
  consultant?.name || consultant?.cname || consultant?.staffId || "-";

const renderConsultants = (consultants = []) => {
  if (!consultants.length) {
    return (
      <Text size="xs" color="dimmed">
        -
      </Text>
    );
  }

  return (
    <Stack spacing={2}>
      {consultants.map((consultant) => (
        <Text
          key={consultant._id || consultant.staffId || consultant.name}
          size="xs"
        >
          {getConsultantLabel(consultant)}
        </Text>
      ))}
    </Stack>
  );
};

const renderClientPayments = (conflict) => {
  const payments = conflict.clientPayments || [];
  if (payments.length) {
    return (
      <Stack spacing={4}>
        {payments.map((cp) => (
          <Stack key={`${cp.code}-${cp.matchedDate || cp.paymentDate}`} spacing={0}>
            <Text size="xs" weight={500}>
              {cp.code}
            </Text>
            <Text size="xs" color="dimmed">
              pay {formatDate(cp.paymentDate)} · matched{" "}
              {formatDate(cp.matchedDate)}
            </Text>
          </Stack>
        ))}
      </Stack>
    );
  }

  if (conflict.cpCode) {
    return (
      <Stack spacing={0}>
        <Text size="xs" weight={500}>
          {conflict.cpCode}
        </Text>
        <Text size="xs" color="dimmed">
          pay {formatDate(conflict.cpPaymentDate)} · matched{" "}
          {formatDate(conflict.cpMatchedDate)}
        </Text>
      </Stack>
    );
  }

  return (
    <Text size="xs" color="dimmed">
      -
    </Text>
  );
};

const renderDnOrder = (conflict) => {
  const orderId =
    conflict.orderId ||
    (conflict.sourceType === "COMMISSIONSTATEMENT" ? conflict.sourceId : null);
  const orderCode =
    conflict.orderCode ||
    (conflict.sourceType === "COMMISSIONSTATEMENT"
      ? conflict.sourceCode
      : null);

  if (conflict.sourceType === "CLIENTPAYMENT") {
    return (
      <Stack spacing={2}>
        <EntityTabAnchor
          entity="debitNote"
          id={conflict.sourceId}
          label={conflict.sourceCode || conflict.sourceId}
          size="xs"
          weight={500}
        />
        {orderId && orderCode ? (
          <EntityTabAnchor
            entity="order"
            id={orderId}
            label={orderCode}
            size="xs"
          />
        ) : null}
      </Stack>
    );
  }

  if (conflict.sourceType === "COMMISSIONSTATEMENT" && orderId && orderCode) {
    return (
      <EntityTabAnchor
        entity="order"
        id={orderId}
        label={orderCode}
        size="xs"
        weight={500}
      />
    );
  }

  return (
    <Text size="xs">{conflict.sourceCode || conflict.sourceId || "-"}</Text>
  );
};

const DuplicateBidConflictsAlert = ({
  conflicts = [],
  title = "Sources already commissioned on other payrolls",
  description = "These sources already have native bids on dispatched payrolls. They will be skipped on this payroll and cannot be commissioned again. Compare pay date vs matched date — late bank matching can move a payment into a later payroll period.",
  color = "orange",
}) => {
  const totalBids = useMemo(
    () =>
      (conflicts || []).reduce(
        (sum, conflict) => sum + (conflict.bidCount || 0),
        0,
      ),
    [conflicts],
  );

  if (!conflicts?.length) return null;

  return (
    <Alert color={color} p="sm">
      <Accordion defaultValue={null} variant="contained" chevronPosition="right">
        <Accordion.Item value="skipped-duplicates">
          <Accordion.Control>
            <Group position="apart" spacing="md" noWrap>
              <Text size="sm" weight={600}>
                {title}
              </Text>
              <Group spacing="lg" noWrap>
                <Text size="sm">
                  Skip Duplicated ({conflicts.length})
                </Text>
                <Text size="sm">Number of Bids ({totalBids})</Text>
              </Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack spacing="sm">
              <Text size="sm">{description}</Text>
              <Box sx={{ overflowX: "auto" }}>
                <Table fontSize="xs" striped withBorder>
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>DN / Order</th>
                      <th>Consultants</th>
                      <th>CP (pay / matched)</th>
                      <th>Dispatched On</th>
                      <th>Bids</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conflicts.map((conflict) => (
                      <tr key={`${conflict.sourceType}-${conflict.sourceId}`}>
                        <td>
                          <Badge
                            size="xs"
                            color={getSourceTypeColor(conflict.sourceType)}
                          >
                            {conflict.sourceType}
                          </Badge>
                        </td>
                        <td>{renderDnOrder(conflict)}</td>
                        <td>{renderConsultants(conflict.consultants)}</td>
                        <td>{renderClientPayments(conflict)}</td>
                        <td>
                          <Group spacing={4}>
                            {(conflict.payrolls || []).map((payroll) => (
                              <Stack key={payroll._id} spacing={2}>
                                <EntityTabAnchor
                                  entity="payroll"
                                  id={payroll._id}
                                  label={payroll.code}
                                  size="xs"
                                  weight={500}
                                />
                                {payroll.status && (
                                  <Badge
                                    size="xs"
                                    color="gray"
                                    variant="outline"
                                  >
                                    {payroll.status}
                                  </Badge>
                                )}
                              </Stack>
                            ))}
                          </Group>
                        </td>
                        <td>{conflict.bidCount || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Box>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Alert>
  );
};

export default DuplicateBidConflictsAlert;
